import React from 'react';
import { Button, Field, Icon } from './primitives.jsx';
import { accentVars } from './branding.js';
import { TipsterEliminator } from './TipsterEliminator.jsx';

const HANDLE_KEY = (poolId) => `sweep-wc26-handle-${poolId}`;

export const BrandedShell = ({ pool }) => {
  const brand = pool.branding || {};
  const games = pool.enabled_games || ['eliminator'];
  const [identity, setIdentity] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(HANDLE_KEY(pool.id))) || null; } catch { return null; }
  });
  const [game, setGame] = React.useState(games[0] || 'eliminator');

  if (!identity) {
    return (
      <Shell brand={brand} pool={pool}>
        <EntryGate pool={pool} brand={brand} onJoined={(id) => {
          try { localStorage.setItem(HANDLE_KEY(pool.id), JSON.stringify(id)); } catch { /* private mode */ }
          setIdentity(id);
        }} />
      </Shell>
    );
  }

  return (
    <Shell brand={brand} pool={pool}>
      <p style={{ color: 'var(--ink-soft)', margin: '0 0 16px' }}>
        Playing as <strong>{identity.displayName}</strong> (@{identity.handle})
      </p>
      {games.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {games.map((g) => (
            <Button key={g} variant={g === game ? 'primary' : 'secondary'} onClick={() => setGame(g)} style={{ textTransform: 'capitalize' }}>
              {g}
            </Button>
          ))}
        </div>
      )}
      {game === 'eliminator'
        ? <TipsterEliminator pool={pool} identity={identity} />
        : <p style={{ color: 'var(--ink-mute)' }}>{game[0].toUpperCase() + game.slice(1)} is coming soon for this brand.</p>}
    </Shell>
  );
};

const Shell = ({ brand, pool, children }) => (
  <section style={{ ...accentVars(brand.accent), padding: '96px 24px 80px', maxWidth: 880, margin: '0 auto' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
      {brand.logoUrl && <img src={brand.logoUrl} alt="" style={{ height: 48, width: 'auto', borderRadius: 8 }} />}
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px,5vw,56px)', textTransform: 'uppercase', margin: 0, lineHeight: 0.95 }}>
        {brand.name || pool?.name || 'Game'}
      </h1>
    </div>
    {brand.prizeBlurb && (
      <div style={{ background: 'var(--blue-pale)', border: '1.5px solid var(--hairline-blue)', borderRadius: 10, padding: '12px 16px', marginBottom: 24, fontSize: 15 }}>
        🏆 {brand.prizeBlurb}
      </div>
    )}
    {children}
  </section>
);

const EntryGate = ({ pool, brand, onJoined }) => {
  const [name, setName] = React.useState('');
  const [handle, setHandle] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const join = async () => {
    if (!name.trim() || !handle.trim()) { setError('Enter your name and Telegram @handle.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`/api/pool/${pool.id}/entrants/join`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), handle: handle.trim() }),
      });
      const body = await res.json();
      if (!res.ok) { setError(body.error || 'Could not join.'); return; }
      onJoined(body);
    } catch { setError('Network error.'); } finally { setLoading(false); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 380 }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, textTransform: 'uppercase', margin: 0 }}>
        Enter {brand.name || 'the game'}
      </h2>
      <Field label="Display name" value={name} onChange={setName} placeholder="e.g. Big Dave" />
      <Field label="Telegram @handle" value={handle} onChange={setHandle} placeholder="@bigdave" hint="Your handle is your identity — use the same one each time." />
      {error && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>}
      <Button variant="primary" onClick={join} disabled={loading}>{loading ? 'JOINING…' : 'JOIN'} <Icon name="arrow-right" size={16} /></Button>
    </div>
  );
};
